import { useEffect, useState } from 'react';
import { FiCopy, FiEye, FiEyeOff, FiLock, FiShield, FiSmartphone } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosInstance';
import { Button } from '../ui/Button';
import { FormField, inputClassName } from '../ui/FormField';
import { DirectoryFormModal } from '../Directory/DirectoryFormModal';
import { AppTooltip } from '../ui/AppTooltip';

export const TwoFactorSecurity = ({ isAdmin = false }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ loading: true, required: false, enabled: false, configured: false });
  const [showEnableForm, setShowEnableForm] = useState(false);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [setup, setSetup] = useState(null);
  const [enablePassword, setEnablePassword] = useState('');
  const [enableCode, setEnableCode] = useState('');
  const [showEnablePassword, setShowEnablePassword] = useState(false);
  const [showDisablePassword, setShowDisablePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    axiosInstance('/user/2fa', 'get')
      .then((response) => {
        if (active && response?.status === 200) setStatus({ loading: false, ...response.data });
      })
      .catch(() => { if (active) setStatus((current) => ({ ...current, loading: false })); });
    return () => { active = false; };
  }, []);

  const closeEnableForm = (force = false) => {
    if (submitting && !force) return;
    setShowEnableForm(false);
    setSetup(null);
    setEnablePassword('');
    setEnableCode('');
    setShowEnablePassword(false);
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('Copied to clipboard'));
    } catch (_error) {
      toast.error(t('Unable to copy'));
    }
  };

  const enable = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (status.configured && (!enablePassword || enableCode.length !== 6)) return;
    if (!status.configured && !setup && !enablePassword) return;
    if (setup && enableCode.length !== 6) return;

    setSubmitting(true);
    try {
      if (status.configured) {
        const response = await axiosInstance('/user/2fa/reactivate', 'post', { password: enablePassword, code: enableCode });
        if (response?.status !== 200) throw new Error(response?.data?.message);
        setStatus((current) => ({ ...current, required: true, enabled: true, configured: true }));
        closeEnableForm(true);
        toast.success(t('Two-factor authentication enabled successfully.'));
        return;
      }

      if (!setup) {
        const response = await axiosInstance('/user/2fa/setup', 'post', { password: enablePassword });
        if (response?.status !== 200) throw new Error(response?.data?.message);
        setSetup(response.data);
        return;
      }

      const response = await axiosInstance('/user/2fa/enable', 'post', { code: enableCode });
      if (response?.status !== 200) throw new Error(response?.data?.message);
      setStatus((current) => ({ ...current, required: true, enabled: true, configured: true }));
      closeEnableForm(true);
      toast.success(t('Two-factor authentication enabled successfully.'));
    } catch (error) {
      toast.error(t(error?.response?.data?.message || error?.message || 'Unable to enable two-factor authentication.'));
    } finally {
      setSubmitting(false);
    }
  };

  const statusLabel = status.loading
    ? t('Loading')
    : status.enabled
      ? t('Authenticator configured and active')
      : status.required
        ? t('Authenticator setup is required at your next sign-in.')
        : t('Two-factor authentication is not required for this account.');

  const disable = async (event) => {
    event.preventDefault();
    if (submitting || !password || code.length !== 6) return;
    setSubmitting(true);
    try {
      const response = await axiosInstance('/user/2fa/disable', 'post', { password, code });
      if (response?.status !== 200) throw new Error(response?.data?.message);
      setStatus((current) => ({ ...current, required: false, enabled: false, configured: true }));
      setShowDisableForm(false);
      setPassword('');
      setCode('');
      toast.success(t('Two-factor authentication disabled successfully.'));
    } catch (error) {
      toast.error(t(error?.response?.data?.message || 'Unable to update two-factor authentication.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className='mt-3 border-t border-[var(--border-subtle)] pt-3'>
      <div className='flex items-center gap-3 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] p-3'>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] ${status.enabled ? 'bg-green-500/10 text-[var(--success)]' : 'bg-[var(--surface-panel)] text-[var(--text-muted)]'}`}>
          {status.enabled ? <FiSmartphone aria-hidden='true' /> : <FiShield aria-hidden='true' />}
        </span>
        <div className='min-w-0'>
          <p className='flex items-center gap-2 text-sm font-bold text-[var(--text-primary)]'><FiLock className='text-[var(--brand)]' aria-hidden='true' />{t('Two-factor authentication')}</p>
          <p className='mt-0.5 text-xs text-[var(--text-secondary)]'>{statusLabel}</p>
        </div>
      </div>
      {isAdmin && !status.loading && !status.enabled && (
        <div className='mt-3'>
          <Button variant='primary' className='min-h-9 w-full py-1.5' onClick={() => setShowEnableForm(true)}>{t('Enable 2FA')}</Button>
        </div>
      )}
      {isAdmin && status.enabled && (
        <div className='mt-3'>
          <Button variant='danger' className='min-h-9 w-full py-1.5' onClick={() => setShowDisableForm(true)}>{t('Disable 2FA')}</Button>
        </div>
      )}
      </div>
      {showEnableForm && <DirectoryFormModal
        open={showEnableForm}
        onClose={closeEnableForm}
        onSubmit={enable}
        title={t('Enable 2FA')}
        description={status.configured ? t('Use the authenticator already configured for this account.') : setup ? t('Scan this QR code with Google Authenticator, Microsoft Authenticator, Authy, or another compatible app.') : t('Confirm your password before configuring an authenticator app.')}
        icon={FiShield}
        submitLabel={status.configured || setup ? t('Verify and enable') : t('Next')}
        cancelLabel={t('Cancel')}
        closeLabel={t('Close')}
        submitting={submitting}
        width={460}
      >
        <div className='space-y-3'>
          {status.configured ? (
            <>
              <FormField label={t('Current Password')}>
                <div className='relative'>
                  <input autoFocus type={showEnablePassword ? 'text' : 'password'} autoComplete='current-password' className={`${inputClassName} pr-11`} value={enablePassword} onChange={(event) => setEnablePassword(event.target.value)} required />
                  <AppTooltip label={showEnablePassword ? t('Hide password') : t('Show password')}><button
                    type='button'
                    onClick={() => setShowEnablePassword((value) => !value)}
                    className='absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-panel-muted)] hover:text-[var(--text-primary)]'
                    aria-label={showEnablePassword ? t('Hide password') : t('Show password')}
                  >
                    {showEnablePassword ? <FiEyeOff aria-hidden='true' /> : <FiEye aria-hidden='true' />}
                  </button></AppTooltip>
                </div>
              </FormField>
              <FormField label={t('Authentication code')}>
                <input inputMode='numeric' autoComplete='one-time-code' pattern='[0-9]{6}' maxLength={6} className={inputClassName} value={enableCode} onChange={(event) => setEnableCode(event.target.value.replace(/\D/g, ''))} required />
              </FormField>
            </>
          ) : !setup ? (
            <FormField label={t('Current Password')}>
              <div className='relative'>
                <input autoFocus type={showEnablePassword ? 'text' : 'password'} autoComplete='current-password' className={`${inputClassName} pr-11`} value={enablePassword} onChange={(event) => setEnablePassword(event.target.value)} required />
                <AppTooltip label={showEnablePassword ? t('Hide password') : t('Show password')}><button
                  type='button'
                  onClick={() => setShowEnablePassword((value) => !value)}
                  className='absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-panel-muted)] hover:text-[var(--text-primary)]'
                  aria-label={showEnablePassword ? t('Hide password') : t('Show password')}
                >
                  {showEnablePassword ? <FiEyeOff aria-hidden='true' /> : <FiEye aria-hidden='true' />}
                </button></AppTooltip>
              </div>
            </FormField>
          ) : (
            <>
              <img src={setup.qrCode} alt={t('QR code for two-factor authentication')} className='mx-auto aspect-square w-44 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-white p-2' />
              <div className='rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] p-3'>
                <p className='text-xs text-[var(--text-muted)]'>{t('Manual setup key')}</p>
                <div className='mt-1 flex items-center gap-2'>
                  <code className='min-w-0 flex-1 break-all text-xs text-[var(--text-primary)]'>{setup.secret}</code>
                  <button type='button' onClick={() => copy(setup.secret)} className='text-[var(--brand)]' aria-label={t('Copy')}><FiCopy /></button>
                </div>
              </div>
              <FormField label={t('Authentication code')}>
                <input autoFocus inputMode='numeric' autoComplete='one-time-code' pattern='[0-9]{6}' maxLength={6} className={inputClassName} value={enableCode} onChange={(event) => setEnableCode(event.target.value.replace(/\D/g, ''))} required />
              </FormField>
            </>
          )}
        </div>
      </DirectoryFormModal>}
      {showDisableForm && <DirectoryFormModal
        open={showDisableForm}
        onClose={() => { if (!submitting) { setShowDisableForm(false); setPassword(''); setCode(''); setShowDisablePassword(false); } }}
        onSubmit={disable}
        title={t('Disable 2FA')}
        description={t('Confirm this sensitive action with your password and authenticator code.')}
        icon={FiShield}
        submitLabel={t('Disable')}
        submitVariant='danger'
        cancelLabel={t('Cancel')}
        closeLabel={t('Close')}
        submitting={submitting}
        width={460}
      >
        <div className='space-y-3'>
          <FormField label={t('Current Password')}>
            <div className='relative'>
              <input autoFocus type={showDisablePassword ? 'text' : 'password'} autoComplete='current-password' className={`${inputClassName} pr-11`} value={password} onChange={(event) => setPassword(event.target.value)} required />
              <AppTooltip label={showDisablePassword ? t('Hide password') : t('Show password')}><button
                type='button'
                onClick={() => setShowDisablePassword((value) => !value)}
                className='absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-panel-muted)] hover:text-[var(--text-primary)]'
                aria-label={showDisablePassword ? t('Hide password') : t('Show password')}
              >
                {showDisablePassword ? <FiEyeOff aria-hidden='true' /> : <FiEye aria-hidden='true' />}
              </button></AppTooltip>
            </div>
          </FormField>
          <FormField label={t('Authentication code')}>
            <input inputMode='numeric' autoComplete='one-time-code' pattern='[0-9]{6}' maxLength={6} className={inputClassName} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} required />
          </FormField>
        </div>
      </DirectoryFormModal>}
    </>
  );
};
